define(['underscore', './_Augment'], (_, Augment)=> {
    const METADATA_CLASS = 'edge.aspects.validation.dto.ValidationRuleDTO';

    /** Validitiy descriptor for any type. Considers value as always valid. */
    const ANY_TYPE = {
        'typeValid': _.constant(true),
        'messages': _.constant([])
    };

    const INT_TYPES = ['byte', 'short', 'int', 'long', 'Byte', 'Short', 'Integer', 'Long', 'BigInteger'];
    const FLOAT_TYPES = ['float', 'double', 'Float', 'Double', 'BigDecimal'];

    /** Creates a new "Field is required" aspect. */
    function create(expressionLanguage, validationMessages) {
        /** Validity for the decimal type. */
        const DECIMAL_TYPE = {
            'typeValid': (val)=> {
                if (_.isNumber(val)) {
                    return true;
                }
                return /^-?\d*(\.\d*)?$/.test(val);
            },
            'messages': (/*val*/)=> {
                return [validationMessages.notANumber];
            }
        };


        /** Validity descriptor for the integer types. */
        const INT_TYPE = {
            'typeValid': (val)=> {
                if (_.isNumber(val)) {
                    return Math.round(val) === val;
                }

                return /^-?\d+$/.test(val);
            },
            'messages': (val)=> {
                if (!DECIMAL_TYPE.typeValid(val)) {
                    return DECIMAL_TYPE.messages(val);
                }
                return [validationMessages.notAnInteger];
            }
        };


        function getAllRules(compilationContext, node, nodeMetadata, ancestorChain) {
            return Augment.collectRules(compilationContext, node, nodeMetadata, ancestorChain, METADATA_CLASS);
        }

        function compileRule(compilationContext, rule) {
            const validationRule = compilationContext.compile(rule.data.expression);
            const messageExpr = compilationContext.compile(rule.data.message);

            return {
                'shouldApply': rule.shouldApply,
                'validate': validationRule,
                'message': messageExpr
            };
        }

        function combineRules(rules) {
            function getMessages(v, pv, ctx) {
                return rules.filter((rule)=> {
                    /* NONSENSE handling: shouldApply do that internally. Negation is fine for it. */
                    return rule.shouldApply() && !rule.validate(v, pv, ctx);
                }).map((rule)=> {
                    return rule.message(v, pv, ctx);
                });
            }

            return {
                'messages': getMessages,
                'valid': (v, pv, ctx)=> {
                    return rules.every((rule)=> {
                        /* NONSENSE handling: shouldApply internally. Explicit check in validate. */
                        return !rule.shouldApply() || (rule.validate(v, pv, ctx) === true);
                    });
                }
            };
        }

        /** Returns a type validation descriptor based on the DTO type. */
        function getTypeDescriptor(type) {
            switch (type.kind) {
                case 'primitive' :
                    if (INT_TYPES.indexOf(type.name) >= 0) {
                        return INT_TYPE;
                    }
                    if (FLOAT_TYPES.indexOf(type.name) >= 0) {
                        return DECIMAL_TYPE;
                    }
                    return ANY_TYPE;

                default :
                    return ANY_TYPE;
            }
        }


        return {
            'getAspectProperties': (currentViewModelNode, currentMetadataNode, ancestorChain)=> {
                const compilationContext = expressionLanguage.getCompilationContext(currentMetadataNode.xCenter);
                const rules = getAllRules(compilationContext, currentViewModelNode, currentMetadataNode, ancestorChain);
                const compiledRules = rules.map(_.partial(compileRule, compilationContext));
                const descriptor = combineRules(compiledRules);
                const typeDescriptor = getTypeDescriptor(currentMetadataNode.valueType);
                const readOnlyProperty = currentMetadataNode.readOnly;

                /* Angular evaluation model workaround. */
                let lastMessages = null;

                function valueNotSet() {
                    const val = currentViewModelNode.value;
                    return _.isUndefined(val) || _.isNull(val) || val === '';
                }

                function calcAllMessages() {
                    if (valueNotSet()) {
                        return currentViewModelNode.aspects._requirednessMessages;
                    }
                    if (!typeDescriptor.typeValid(currentViewModelNode.value)) {
                        return typeDescriptor.messages(currentViewModelNode.value);
                    }
                    return descriptor.messages(currentViewModelNode,
                        ancestorChain.parent, currentViewModelNode.aspects.context);
                }

                function allMessages() {
                    if (readOnlyProperty) {
                        return [];
                    }
                    const newMessages = _.unique(calcAllMessages());
                    if (!_.isEqual(lastMessages, newMessages)) {
                        lastMessages = newMessages;
                    }
                    return lastMessages;
                }

                return {
                    'valid': {
                        'get': ()=> {
                            if (readOnlyProperty) {
                                return true;
                            }
                            if (valueNotSet()) {
                                return !currentViewModelNode.aspects.required;
                            }
                            return typeDescriptor.typeValid(currentViewModelNode.value) &&
                                descriptor.valid(currentViewModelNode,
                                    ancestorChain.parent, currentViewModelNode.aspects.context);
                        }
                    },
                    'validationMessages': {
                        'get': allMessages
                    },
                    'validationMessage': {
                        'get': ()=> {
                            return allMessages()[0];
                        }
                    }
                };
            }
        };
    }

    return {
        create
    };
});


// WEBPACK FOOTER //
// ../common/js/src/edge/platform/viewmodel/aspects/Validity.js